import type { PaymentIntentError } from "@checkout-customer-api/model/paymentIntent.model";

export interface Props {
  readonly error?: string | PaymentIntentError;
}

const DEFAULT_MESSAGE = "Something went wrong. Please try again later.";

export const GENERIC_PAYMENT_FAILURE_MESSAGE =
  "This payment could not be processed. Please try a different payment method.";

const DECLINE_REASON_MESSAGES: Readonly<Record<string, string>> = {
  INSUFFICIENT_FUNDS:
    "This payment failed due to insufficient funds. Please ensure your account has enough funds before trying again.",
  DO_NOT_HONOR: "This payment has been declined. Please contact your card issuer.",
  SOFT_DECLINE: "This payment has been declined. Please contact your card issuer.",
  OTHER: "This payment has been declined. Please contact your card issuer.",
};

function resolveDeclineReasonMessage(code?: string): string | undefined {
  return code ? DECLINE_REASON_MESSAGES[code] : undefined;
}

// Treats an empty string the same as "no text", so a blank error.text (or a blank plain-string
// error) still falls through to a real fallback instead of rendering a blank message.
function nonEmpty(text?: string): string | undefined {
  return text === "" ? undefined : text;
}

function isCancelledAuthentication(error: PaymentIntentError): boolean {
  return (
    error.type === "AUTHENTICATION_FAILURE" && error.code === "AUTHENTICATION_CHALLENGE_CANCELLED"
  );
}

export default function ErrorMessage({ error = DEFAULT_MESSAGE }: Readonly<Props>) {
  if (typeof error !== "string" && isCancelledAuthentication(error)) {
    return null;
  }

  const message =
    typeof error === "string"
      ? (nonEmpty(error) ?? DEFAULT_MESSAGE)
      : (nonEmpty(error.text) ??
        resolveDeclineReasonMessage(error.code) ??
        GENERIC_PAYMENT_FAILURE_MESSAGE);

  return (
    <div className="error" role="alert">
      {message}
    </div>
  );
}
